import {
  NameHelpers,
  Patient,
  toServiceCodeItemToothId,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  IMohHighCariesData,
  IMohPreApprovalCode,
  IMohPreApprovalData,
  IPatient,
  IPractice,
  IPricedServiceCodeEntry,
  isEventable,
  IStaffer,
  ITreatmentStep,
  MOH_DATE_FORMAT,
  WithEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  toMoment,
  WithRef,
} from '@principle-theorem/shared';
import { groupBy, sum, toPairs } from 'lodash';
import * as moment from 'moment-timezone';
import { MohPreApprovalRequestForm } from '../../components/moh/moh-send-pre-approval-dialog/moh-send-pre-approval-dialog.component';
import { MohAgreementHolderForm } from '../moh-agreement-holder-form';

export class MohPreApprovalPdfDataBuilder {
  static getPreApprovalData(
    formData: MohPreApprovalRequestForm,
    stepRef: DocumentReference<ITreatmentStep>,
    practitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>,
    patient: WithRef<IPatient>,
    codes: IPricedServiceCodeEntry[]
  ): IMohPreApprovalData {
    const agreementHolder = MohAgreementHolderForm.toAgreementHolder(
      formData.agreementHolder,
      practice
    );
    return {
      stepRef,
      practitionerName: practitioner.user.name,
      dcnzNumber: practitioner.metadata?.nz?.providerDetails?.dcnzNumber ?? '',
      practiceAddress: practice.address,
      patientName: patient.name,
      patientDateOfBirth: patient.dateOfBirth ?? '',
      nhiNumber: formData.nhiNumber ?? patient.nationalHealthIndexNumber ?? '',
      codes: this._toPreApprovalCodes(codes),
      agreementNumber: agreementHolder.agreementNumber,
      claimType: formData.claimType,
      payeeNumber: agreementHolder.payeeNumber,
      comments: formData.comments,
    };
  }

  static async getHighCariesData(
    formData: MohPreApprovalRequestForm,
    patient: WithRef<IPatient>,
    practitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>
  ): Promise<IMohHighCariesData | undefined> {
    const highCariesData = formData.highCariesData;
    if (!highCariesData) {
      return;
    }
    const priorAppointments =
      await Patient.getAppointmentsOrderByEvent(patient);
    const previousExaminationDates =
      priorAppointments
        .filter(
          (appointment): appointment is WithEvent<WithRef<IAppointment>> =>
            isEventable(appointment) &&
            moment(appointment.event.from).isBefore(moment(), 'days')
        )
        .map((appointment) =>
          toMoment(appointment.event.from).format(MOH_DATE_FORMAT)
        )
        .join(', ') ?? '';
    const agreementHolder = MohAgreementHolderForm.toAgreementHolder(
      formData.agreementHolder,
      practice
    );

    return {
      agreementNumber: agreementHolder.agreementNumber,
      patientLastName: NameHelpers.lastName(patient.name),
      patientFirstName: NameHelpers.firstName(patient.name),
      patientNhiNumber:
        formData.nhiNumber ?? patient.nationalHealthIndexNumber ?? '',
      practitionerName: practitioner.user.name,
      previousExaminationDates,
      ...highCariesData,
    };
  }

  private static _toPreApprovalCodes(
    codes: IPricedServiceCodeEntry[]
  ): IMohPreApprovalCode[] {
    const grouped = groupBy(codes, 'code');
    return toPairs(grouped).map(([code, items]) => ({
      code,
      quantity: sum(items.map((item) => item.quantity)),
      price: items[0].price ?? 0,
      toothNumbers: items
        .map((item) => toServiceCodeItemToothId(item))
        .join(', '),
    }));
  }
}
